// Admin_Dashboard.jsx — COMPLETE FULLY UNABBREVIATED
// UPDATED: Full Revenue Breakdown, Live USD/NGN Rate, Three Model Charts + Bar/Curve Toggle (April 2026)

import React, { useState, useEffect, useMemo } from 'react';
import {
  collection, onSnapshot, doc, serverTimestamp,
  query, where, orderBy, increment, writeBatch, limit,
  updateDoc, getDocs, addDoc, getDoc, setDoc, deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

// ── Configuration & Constants ─────────────────────────────────────────────────
const GOLD         = '#B8860B';
const NAVY         = '#001F3F';
const CHART_HEIGHT = 200;
const CHART_WIDTH  = 700;
const SUPER_ADMINS = ["FFXDokpj5IXR3SFSvJyDu70LRp73", "GbjKMdeVEmTgMefcU1cEHsEOstn1"];

// AI cost per successful call in USD — update as Replicate/Anthropic pricing changes
const AI_COSTS_USD = {
  rodin:  0.40,   // Hyper3D Rodin     — 3D head reconstruction (Replicate)
  flux:   0.08,   // FLUX Fill Pro     — hair inpainting (Replicate)
  claude: 0.005,  // Claude Sonnet     — vision hair analysis (Anthropic)
};

const FALLBACK_USD_NGN = 1400; // used if live rate fetch fails

// ── Utility: compact number formatter ────────────────────────────────────────
const formatCompact = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1)     + 'K';
  return Math.round(n).toLocaleString();
};

// ── Utility: group Firestore docs by time period ──────────────────────────────
const groupDocsByPeriod = (dataObjects, tf) => {
  const groups = {};
  dataObjects.forEach(d => {
    const date = d.createdAt?.toDate?.();
    if (!date) return;
    let label;
    if (tf === 'daily') {
      label = date.toLocaleDateString('en-NG', { weekday: 'short', month: 'short', day: 'numeric' });
    } else if (tf === 'weekly') {
      const s = new Date(date);
      s.setDate(s.getDate() - s.getDay());
      label = 'Wk ' + s.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
    } else {
      label = date.toLocaleDateString('en-NG', { month: 'short', year: '2-digit' });
    }
    groups[label] = (groups[label] || 0) + 1;
  });
  return groups;
};

// ── Utility: smooth bezier path from SVG points ───────────────────────────────
const makeBezierPath = (points) => {
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const cp1x = points[i].x + (points[i + 1].x - points[i].x) / 2;
    d += ` C ${cp1x} ${points[i].y}, ${cp1x} ${points[i + 1].y}, ${points[i + 1].x} ${points[i + 1].y}`;
  }
  return d;
};

// ═════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function AdminDashboard() {

  // ── Navigation & UI state ─────────────────────────────────────────────────
  const [activeTab,           setActiveTab]           = useState('overview');
  const navigate                                      = useNavigate();
  const [loading,             setLoading]             = useState(true);
  const [broadcastMessage,    setBroadcastMessage]    = useState('');
  const [isMaintenanceActive, setIsMaintenanceActive] = useState(false);
  const [timeframe,           setTimeframe]           = useState('daily');
  const [aiOpsTimeframe,      setAiOpsTimeframe]      = useState('weekly');
  const [chartType,           setChartType]           = useState('bar');   // 'bar' | 'curve'

  // ── Core data state ───────────────────────────────────────────────────────
  const [historyData,  setHistoryData]  = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [adminUsers,   setAdminUsers]   = useState([]);
  const [isSuper,      setIsSuper]      = useState(false);
  const [stats,        setStats]        = useState({
    totalCommissions: 0, totalScans: 0, totalBookings: 0,
    premiumRevenue: 0, boostRevenue: 0, activePremiumUsers: 0,
  });
  const [scanTrends, setScanTrends] = useState([0,0,0,0,0,0,0]);
  const [metricsDoc, setMetricsDoc] = useState(null);

  // ── AI cost tracking state ────────────────────────────────────────────────
  const [usdToNgn,          setUsdToNgn]         = useState(FALLBACK_USD_NGN);
  const [rateUpdatedAt,     setRateUpdatedAt]    = useState(null);
  const [allTimeRodinCount, setAllTimeRodinCount]= useState(0);
  const [allTimeTryonCount, setAllTimeTryonCount]= useState(0);
  const [rodinData,         setRodinData]        = useState([]);
  const [fluxData,          setFluxData]         = useState([]);
  const [claudeData,        setClaudeData]       = useState([]);

  // ── Revenue breakdown state ───────────────────────────────────────────────
  const [revenueBreakdown, setRevenueBreakdown] = useState({
    bookingCommissions: 0,
    monthlySubRevenue:  0,
    yearlySubRevenue:   0,
    broadcastRevenue:   0,
    promotionRevenue:   0,
  });

  // ── List data state ───────────────────────────────────────────────────────
  const [transactions,  setTransactions]  = useState([]);
  const [payouts,       setPayouts]       = useState([]);
  const [tickets,       setTickets]       = useState([]);
  const [disputes,      setDisputes]      = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery,   setSearchQuery]   = useState('');

  // ── Live USD/NGN rate fetch ───────────────────────────────────────────────
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res  = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await res.json();
        if (data?.rates?.NGN) { setUsdToNgn(data.rates.NGN); setRateUpdatedAt(new Date()); }
      } catch { /* keep fallback */ }
    };
    fetchRate();
    const iv = setInterval(fetchRate, 60 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  // ── Auth & all data subscriptions ─────────────────────────────────────────
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const isSuperUser = SUPER_ADMINS.includes(user.uid);
        setIsSuper(isSuperUser);
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const userData = userSnap.data();
        if (!isSuperUser && userData?.role !== 'admin') { navigate('/'); return; }

        // Metrics
        const unsubMetrics = onSnapshot(doc(db, 'admin', 'ai_metrics'), (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setMetricsDoc(data);
            let trendKey = 'weeklyScanArray';
            if (timeframe === 'daily')   trendKey = 'dailyScanArray';
            if (timeframe === 'monthly') trendKey = 'monthlyScanArray';
            let trends = data[trendKey] || [];
            if (!Array.isArray(trends) || trends.length === 0) {
              if      (trendKey === 'dailyScanArray'   && data.daily)   trends = Object.values(data.daily).slice(-7);
              else if (trendKey === 'weeklyScanArray'  && data.weekly)  trends = Object.values(data.weekly).slice(-7);
              else if (trendKey === 'monthlyScanArray' && data.monthly) trends = Object.values(data.monthly).slice(-7);
              else trends = [0,0,0,0,0,0,0];
            }
            while (trends.length < 7) trends.push(0);
            setScanTrends(trends.slice(-7));
            if (data.totalScans !== undefined) setStats(s => ({...s, totalScans: data.totalScans}));
          } else setScanTrends([0,0,0,0,0,0,0]);
        });

        // Finances doc
        const unsubFin = onSnapshot(doc(db, 'admin', 'finances'), (snap) => {
          if (snap.exists()) {
            const d = snap.data();
            setStats(d);
            setRevenueBreakdown(prev => ({
              ...prev,
              bookingCommissions: d.totalCommissions   || 0,
              monthlySubRevenue:  d.monthlySubRevenue  || 0,
              yearlySubRevenue:   d.yearlySubRevenue   || 0,
              broadcastRevenue:   d.broadcastRevenue   || 0,
              promotionRevenue:   d.promotionRevenue   || d.boostRevenue || 0,
            }));
          }
        });

        // Broadcast payments (stylists messaging clients)
        const unsubBroadcast = onSnapshot(
          query(collection(db, 'broadcast_payments'), where('status', '==', 'paid')),
          (snap) => {
            const total = snap.docs.reduce((s, d) => s + (d.data().amount || 0), 0);
            setRevenueBreakdown(prev => ({ ...prev, broadcastRevenue: total }));
          }
        );

        // Promotion payments (stylists promoting styles on homepage)
        const unsubPromo = onSnapshot(
          query(collection(db, 'promotion_payments'), where('status', '==', 'paid')),
          (snap) => {
            const total = snap.docs.reduce((s, d) => s + (d.data().amount || 0), 0);
            setRevenueBreakdown(prev => ({ ...prev, promotionRevenue: total }));
          }
        );

        // Subscription breakdown from transactions
        const unsubSubs = onSnapshot(
          query(collection(db, 'transactions'), where('type', 'in', ['MONTHLY_SUB','YEARLY_SUB','PREMIUM_UPGRADE']), where('status', '==', 'SUCCESS')),
          (snap) => {
            let monthly = 0, yearly = 0;
            snap.docs.forEach(d => {
              const data = d.data();
              if      (data.type === 'MONTHLY_SUB')                   monthly += (data.amount || 0);
              else if (data.type === 'YEARLY_SUB')                    yearly  += (data.amount || 0);
              else if ((data.amount || 0) > 5000)                     yearly  += (data.amount || 0);
              else                                                     monthly += (data.amount || 0);
            });
            setRevenueBreakdown(prev => ({ ...prev, monthlySubRevenue: monthly, yearlySubRevenue: yearly }));
          }
        );

        const unsubMaint = onSnapshot(doc(db, 'system', 'maintenance'), (snap) => {
          if (snap.exists()) setIsMaintenanceActive(snap.data().active);
        });

        const unsubLive = onSnapshot(query(collection(db, 'live_sessions')), (snap) => {
          setLiveSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        let unsubStaff = () => {};
        if (isSuperUser) {
          unsubStaff = onSnapshot(
            query(collection(db, 'users'), where('role', '==', 'admin')),
            snap => setAdminUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
          );
        }

        const unsubTrans = onSnapshot(
          query(collection(db, 'transactions'), orderBy('createdAt', 'desc'), limit(100)),
          snap => setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        const unsubTickets = onSnapshot(
          query(collection(db, 'tickets'), orderBy('status', 'desc')),
          snap => setTickets(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        const unsubDisputes = onSnapshot(
          query(collection(db, 'bookings'), where('isDisputed', '==', true)),
          snap => setDisputes(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        const unsubPayouts = onSnapshot(
          query(collection(db, 'payouts'), where('status', 'in', ['pending', 'processing'])),
          snap => setPayouts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        const unsubRodinAll = onSnapshot(
          query(collection(db, 'ai_scans'), limit(5000)),
          snap => setAllTimeRodinCount(snap.size)
        );
        const unsubTryonAll = onSnapshot(
          query(collection(db, 'style_tryons'), limit(5000)),
          snap => setAllTimeTryonCount(snap.size)
        );

        // Sweep stale sessions
        const sweepStale = async () => {
          const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);
          const staleSnap   = await getDocs(query(collection(db, 'live_sessions'), where('lastUpdated', '<', twoHoursAgo)));
          staleSnap.forEach(d => deleteDoc(doc(db, 'live_sessions', d.id)));
        };
        sweepStale();

        setLoading(false);

        return () => {
          unsubMetrics(); unsubMaint();    unsubFin();       unsubLive();
          unsubStaff();   unsubTrans();    unsubTickets();   unsubDisputes();
          unsubPayouts(); unsubRodinAll(); unsubTryonAll();
          unsubBroadcast(); unsubPromo();  unsubSubs();
        };
      } else navigate('/login');
    });
    return () => unsubAuth();
  }, [navigate, timeframe]);

  // ── AI OPS chart data ─────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'ai_ops') return;
    const now = new Date();
    let cutoffDate;
    if      (aiOpsTimeframe === 'daily')   cutoffDate = new Date(now.getTime() - 7   * 86400000);
    else if (aiOpsTimeframe === 'weekly')  cutoffDate = new Date(now.getTime() - 28  * 86400000);
    else if (aiOpsTimeframe === 'monthly') cutoffDate = new Date(now.getTime() - 365 * 86400000);
    else                                    cutoffDate = new Date('2024-01-01');

    const cutoffTs = Timestamp.fromDate(cutoffDate);

    const unsubRodin = onSnapshot(
      query(collection(db, 'ai_scans'), where('createdAt', '>=', cutoffTs), orderBy('createdAt', 'asc')),
      (snap) => {
        const groups = groupDocsByPeriod(snap.docs.map(d => d.data()), aiOpsTimeframe);
        setRodinData(Object.entries(groups).map(([label, calls]) => ({
          label, calls, costNgn: calls * AI_COSTS_USD.rodin * usdToNgn,
        })));
      }
    );

    const unsubTryon = onSnapshot(
      query(collection(db, 'style_tryons'), where('createdAt', '>=', cutoffTs), orderBy('createdAt', 'asc')),
      (snap) => {
        const groups = groupDocsByPeriod(snap.docs.map(d => d.data()), aiOpsTimeframe);
        setFluxData(Object.entries(groups).map(([label, calls]) => ({
          label, calls, costNgn: calls * AI_COSTS_USD.flux * usdToNgn,
        })));
        setClaudeData(Object.entries(groups).map(([label, calls]) => ({
          label, calls, costNgn: calls * AI_COSTS_USD.claude * usdToNgn,
        })));
      }
    );

    return () => { unsubRodin(); unsubTryon(); };
  }, [activeTab, aiOpsTimeframe, usdToNgn]);

  // ── Legacy historyData ────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'ai_ops') return;
    const now      = new Date();
    const daysBack = timeframe === 'daily' ? 1 : timeframe === 'weekly' ? 7 : 30;
    const cutoff   = Timestamp.fromDate(new Date(now.setDate(now.getDate() - daysBack)));
    const q        = query(collection(db, 'scans'), where('createdAt', '>=', cutoff), orderBy('createdAt', 'asc'));
    const unsub    = onSnapshot(q, (snap) => {
      const countsMap = {};
      snap.docs.forEach(d => {
        const date = d.data().createdAt?.toDate();
        if (!date) return;
        const label = timeframe === 'daily' ? date.getHours() + ':00' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        countsMap[label] = (countsMap[label] || 0) + 1;
      });
      const dataArray = Object.values(countsMap);
      setHistoryData(dataArray.length > 0 ? dataArray : [0,0,0,0]);
    });
    return () => unsub();
  }, [timeframe, activeTab]);

  // ── Chart path math (legacy Bezier) ──────────────────────────────────────
  const chartPath = useMemo(() => {
    const data   = historyData.length > 1 ? historyData : [0,0,0,0,0];
    const maxVal = Math.max(...data, 10);
    const points = data.map((val, i) => ({
      x: (i / (data.length - 1)) * CHART_WIDTH,
      y: CHART_HEIGHT - (val / maxVal) * (CHART_HEIGHT - 60) - 30,
    }));
    let line = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const cp1x = points[i].x + (points[i+1].x - points[i].x) / 2;
      line += ` C ${cp1x} ${points[i].y}, ${cp1x} ${points[i+1].y}, ${points[i+1].x} ${points[i+1].y}`;
    }
    return { line, area: `${line} L ${CHART_WIDTH} ${CHART_HEIGHT} L 0 ${CHART_HEIGHT} Z`, maxVal };
  }, [historyData]);

  const activeData = useMemo(() => (
    Array.isArray(scanTrends) && scanTrends.length > 0 ? scanTrends : [0,0,0,0,0,0,0]
  ), [scanTrends]);

  const getPathData = () => {
    if (!activeData.length) return { line: '', area: '', maxVal: 10 };
    const width = 700, height = 150;
    const maxVal = Math.max(...activeData, 10);
    const stepX  = width / (activeData.length - 1 || 1);
    const points = activeData.map((val, i) => ({
      x: i * stepX, y: height - (val / maxVal) * (height - 20),
    }));
    const line = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`;
    return { line, area: `${line} L ${width},${height} L 0,${height} Z`, maxVal };
  };
  const { line: dLine, area: dArea, maxVal } = getPathData();

  // ── Computed AI burn ──────────────────────────────────────────────────────
  const rodinBurnUsd  = allTimeRodinCount * AI_COSTS_USD.rodin;
  const fluxBurnUsd   = allTimeTryonCount * AI_COSTS_USD.flux;
  const claudeBurnUsd = allTimeTryonCount * AI_COSTS_USD.claude;
  const totalBurnUsd  = rodinBurnUsd + fluxBurnUsd + claudeBurnUsd;
  const rodinBurnNgn  = rodinBurnUsd  * usdToNgn;
  const fluxBurnNgn   = fluxBurnUsd   * usdToNgn;
  const claudeBurnNgn = claudeBurnUsd * usdToNgn;
  const totalBurnNgn  = totalBurnUsd  * usdToNgn;

  // ── Revenue & profit ──────────────────────────────────────────────────────
  const platformFees    = revenueBreakdown.bookingCommissions;
  const subscriptionRev = revenueBreakdown.monthlySubRevenue + revenueBreakdown.yearlySubRevenue;
  const broadcastRev    = revenueBreakdown.broadcastRevenue;
  const promotionRev    = revenueBreakdown.promotionRevenue;
  const totalRevenue    = platformFees + subscriptionRev + broadcastRev + promotionRev;
  const aiBurnRate      = totalBurnNgn;
  const netProfit       = totalRevenue - aiBurnRate;

  // Period totals
  const rodinPeriodCost  = rodinData.reduce((s, d)  => s + d.costNgn, 0);
  const fluxPeriodCost   = fluxData.reduce((s, d)   => s + d.costNgn, 0);
  const claudePeriodCost = claudeData.reduce((s, d) => s + d.costNgn, 0);
  const totalPeriodCost  = rodinPeriodCost + fluxPeriodCost + claudePeriodCost;

  const tfLabel = { daily: 'Last 7 Days', weekly: 'Last 4 Weeks', monthly: 'Last 12 Months', alltime: 'All Time' }[aiOpsTimeframe];

  const formatNaira = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount || 0).replace('NGN', '₦');

  // ── Handlers ─────────────────────────────────────────────────────────────
  const terminateLiveSession = async (id) => {
    if (window.confirm("Force-terminate this active 3D projection?")) {
      try { await deleteDoc(doc(db, 'live_sessions', id)); toast.success("Projection Terminated"); }
      catch { toast.error("Termination failed"); }
    }
  };

  const processPayoutRequest = async (payoutId) => {
    try {
      const batch      = writeBatch(db);
      const payoutRef  = doc(db, 'payouts', payoutId);
      const payoutSnap = await getDoc(payoutRef);
      const payoutData = payoutSnap.data();
      batch.update(payoutRef, { status: 'processing', processedAt: serverTimestamp(), adminNote: 'Sent to banking queue' });
      batch.set(doc(collection(db, 'system_alerts')), {
        userId: payoutData.stylistId, type: 'PAYOUT_UPDATE', title: 'Withdrawal Processing',
        message: `Your withdrawal of ${formatNaira(payoutData.amount)} is now being processed.`,
        createdAt: serverTimestamp(), read: false,
      });
      await batch.commit();
      toast.success("Payout moved to processing bank queue");
    } catch (err) { console.error(err); toast.error("Status update failed"); }
  };

  const finalApprovePayout = async (payout) => {
    if (!window.confirm(`Confirm ₦${payout.amount} has been wired to ${payout.stylistName}?`)) return;
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'payouts', payout.id),      { status: 'paid', completedAt: serverTimestamp() });
      batch.update(doc(db, 'users', payout.stylistId), { balance: increment(-payout.amount), totalWithdrawn: increment(payout.amount) });
      batch.update(doc(db, 'admin', 'finances'),        { totalPayoutsDistributed: increment(payout.amount) });
      batch.set(doc(collection(db, 'system_alerts')), {
        userId: payout.stylistId, type: 'PAYOUT_SUCCESS', title: 'Funds Dispatched',
        message: `₦${payout.amount} has been sent to your ${payout.bankName} account.`,
        createdAt: serverTimestamp(), read: false, sendEmail: true,
      });
      await batch.commit();
      toast.success("Payout finalized.");
    } catch (err) { console.error(err); toast.error("Finalization failed"); }
  };

  const rejectPayoutRequest = async (payoutId) => {
    const reason = window.prompt("Reason for rejection (sent to stylist):");
    if (reason === null) return;
    try {
      const batch      = writeBatch(db);
      const payoutSnap = await getDoc(doc(db, 'payouts', payoutId));
      const payoutData = payoutSnap.data();
      batch.update(doc(db, 'payouts', payoutId), { status: 'rejected', rejectionReason: reason, rejectedAt: serverTimestamp() });
      batch.set(doc(collection(db, 'system_alerts')), {
        userId: payoutData.stylistId, type: 'PAYOUT_REJECTED', title: 'Withdrawal Declined',
        message: `Your withdrawal was declined. Reason: ${reason}`,
        createdAt: serverTimestamp(), read: false, sendEmail: true,
      });
      await batch.commit();
      toast.success("Payout rejected and stylist notified.");
    } catch { toast.error("Rejection failed"); }
  };

  const exportFinancesToCSV = () => {
    const headers = ['Transaction ID','Amount','Type','Status','Date'];
    const rows    = transactions.map(t => [t.id, t.amount, t.type||'N/A', t.status, t.createdAt?.toDate().toLocaleDateString()||'']);
    const csv     = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(e => e.join(",")).join("\n");
    const link    = document.createElement("a");
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute("download", `ESDRAS_FINANCIAL_REPORT_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    toast.success("CSV Report Downloaded");
  };

  const toggleMaintenance = async () => {
    if (!isSuper) return toast.error("Super Admin Authority Required");
    const newState = !isMaintenanceActive;
    if (window.confirm(`Set System Maintenance to ${newState ? 'ON' : 'OFF'}?`)) {
      await setDoc(doc(db, 'system', 'maintenance'), { active: newState, updatedAt: serverTimestamp() }, { merge: true });
      toast.success(`Maintenance ${newState ? 'ON' : 'OFF'}`);
    }
  };

  const sendGlobalBroadcast = async () => {
    if (!broadcastMessage.trim()) return toast.error("Enter a message");
    try {
      await addDoc(collection(db, 'system_alerts'), { message: broadcastMessage, target: 'all', createdAt: serverTimestamp(), sender: 'SUPER_ADMIN' });
      setBroadcastMessage('');
      toast.success("Global Broadcast Sent!");
    } catch { toast.error("Broadcast failed"); }
  };

  const handleTicketReply = async (ticketId, replyText) => {
    if (!replyText) return;
    const snap            = await getDoc(doc(db, 'tickets', ticketId));
    const currentMessages = snap.data().messages || [];
    await updateDoc(doc(db, 'tickets', ticketId), {
      messages: [...currentMessages, { text: replyText, sender: 'ADMIN', createdAt: new Date() }],
      status: 'replied', lastAdminReply: serverTimestamp(),
    });
    toast.success("Reply Sent");
  };

  const closeTicket       = async (id)  => { await updateDoc(doc(db, 'tickets', id), { status: 'closed' }); toast.success("Ticket Closed"); };
  const grantAdminAccess  = async (uid) => { if (!isSuper) return; await updateDoc(doc(db, 'users', uid), { role: 'admin', adminAccessLevel: { support: true, finances: false } }); toast.success("Staff Appointed"); };
  const revokeAdminAccess = async (uid) => { if (!isSuper) return; await updateDoc(doc(db, 'users', uid), { role: 'user', adminAccessLevel: null }); toast.success("Access Revoked"); };

  if (loading) return <div style={fullPageCenter}>ESTABLISHING SECURE PROTOCOL...</div>;

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={containerStyle}>
      <style>{`
        @keyframes pulse-live {
          0%   { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0,255,0,0.7); }
          70%  { transform: scale(1);    box-shadow: 0 0 0 6px rgba(0,255,0,0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0,255,0,0); }
        }
      `}</style>

      {/* ── TOP NAV ──────────────────────────────────────────────────────── */}
      <nav style={topNav}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <h2 style={{ color: GOLD, fontSize: '0.9rem', margin: 0, letterSpacing: '2px' }}>ESDRAS BRIDGE</h2>
          <button onClick={() => navigate('/')}          style={navBtn}>SITE</button>
          <button onClick={() => navigate('/dashboard')} style={navBtn}>USER VIEW</button>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={rateBadge}>
            <span style={{ opacity: 0.45, fontSize: '0.5rem', letterSpacing: '1px' }}>LIVE RATE</span>
            <span style={{ color: GOLD, fontWeight: '900', fontSize: '0.75rem' }}>
              $1 = ₦{usdToNgn.toLocaleString('en-NG', { maximumFractionDigits: 0 })}
            </span>
            {rateUpdatedAt && (
              <span style={{ opacity: 0.3, fontSize: '0.48rem' }}>
                {rateUpdatedAt.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          <button onClick={exportFinancesToCSV} style={exportBtn}>EXPORT CSV</button>
          {isSuper && (
            <button onClick={toggleMaintenance} style={{ ...lockdownBtn, borderColor: isMaintenanceActive ? '#ff4d4d' : GOLD }}>
              {isMaintenanceActive ? "DISABLE LOCK" : "LOCK SYSTEM"}
            </button>
          )}
        </div>
      </nav>

      <div style={{ padding: '2rem' }}>

        {/* ── TAB NAV ────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '30px', overflowX: 'auto', paddingBottom: '10px' }}>
          <button onClick={() => setActiveTab('overview')}  style={activeTab === 'overview'  ? activeBtn : inactiveBtn}>FINANCES</button>
          <button onClick={() => setActiveTab('telemetry')} style={activeTab === 'telemetry' ? activeBtn : inactiveBtn}>
            LIVE TELEMETRY {liveSessions.length > 0 && <span style={inlineBadge}>{liveSessions.length}</span>}
          </button>
          <button onClick={() => setActiveTab('ai_ops')}   style={activeTab === 'ai_ops'    ? activeBtn : inactiveBtn}>AI OPS</button>
          <button onClick={() => setActiveTab('support')}  style={activeTab === 'support'   ? activeBtn : inactiveBtn}>
            SUPPORT {tickets.filter(t => t.status === 'open').length > 0 && <span style={inlineBadge}>{tickets.filter(t => t.status === 'open').length}</span>}
          </button>
          <button onClick={() => setActiveTab('users')}    style={activeTab === 'users'     ? activeBtn : inactiveBtn}>MODERATION</button>
          {isSuper && <button onClick={() => setActiveTab('staff')} style={activeTab === 'staff' ? activeBtn : inactiveBtn}>STAFF ACCESS</button>}
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            FINANCES TAB
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>

            {/* KPI Row */}
            <div style={kpiGrid}>
              <KPIBox label="EST. NET PROFIT"  value={formatNaira(netProfit)}            color={netProfit >= 0 ? GOLD : '#ff4d4d'} />
              <KPIBox label="TOTAL REVENUE"    value={formatNaira(totalRevenue)}          color="#2ecc71" />
              <KPIBox label="TOTAL AI BURN"    value={formatNaira(aiBurnRate)}            color="#ff4d4d" />
              <KPIBox label="PREMIUM USERS"    value={stats.activePremiumUsers || 0}      color="#00bbff" />
              <KPIBox label="LIVE SESSIONS"    value={liveSessions.length}                color="#a78bfa" />
            </div>

            {/* ── Revenue Breakdown ──────────────────────────────────────── */}
            <Section title="📊 REVENUE BREAKDOWN — ALL TIME">
              <div style={rateInfoBar}>
                <span style={{ fontSize: '0.62rem', opacity: 0.5 }}>Live market rate:</span>
                <span style={{ color: GOLD, fontWeight: '900', fontSize: '0.78rem' }}>
                  $1 USD = ₦{usdToNgn.toLocaleString('en-NG', { maximumFractionDigits: 0 })}
                </span>
                {rateUpdatedAt && (
                  <span style={{ fontSize: '0.55rem', opacity: 0.35 }}>
                    Refreshed: {rateUpdatedAt.toLocaleString('en-NG', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                  </span>
                )}
              </div>

              <div style={ledgerTable}>
                {/* Header */}
                <div style={{ ...ledgerHeader, gridTemplateColumns: '2.5fr 2fr 1.2fr' }}>
                  <span>REVENUE STREAM</span>
                  <span>DESCRIPTION</span>
                  <span style={{ textAlign: 'right' }}>AMOUNT (NGN)</span>
                </div>

                {/* Platform / Booking Fees */}
                <div style={{ ...revRow, borderLeft: '3px solid #2ecc71' }}>
                  <div>
                    <div style={modelName}>🏦 Platform Fees (10% Booking Commission)</div>
                    <div style={modelSub}>10% of every completed booking auto-routed to ESDRAS</div>
                  </div>
                  <div style={{ fontSize: '0.68rem', opacity: 0.45 }}>
                    Deducted at booking completion via escrow engine
                  </div>
                  <div style={{ ...ledgerVal, color: '#2ecc71', fontWeight: '900', textAlign: 'right', fontSize: '0.92rem' }}>
                    {formatNaira(platformFees)}
                  </div>
                </div>

                {/* Monthly Subscriptions */}
                <div style={{ ...revRow, borderLeft: '3px solid #00bbff' }}>
                  <div>
                    <div style={modelName}>📅 Monthly Subscriptions</div>
                    <div style={modelSub}>SHIELD plan — ₦3,000/month per subscriber</div>
                  </div>
                  <div style={{ fontSize: '0.68rem', opacity: 0.45 }}>
                    Recurring · 4 AI scans/month + unlimited try-ons
                  </div>
                  <div style={{ ...ledgerVal, color: '#00bbff', fontWeight: '900', textAlign: 'right', fontSize: '0.92rem' }}>
                    {formatNaira(revenueBreakdown.monthlySubRevenue)}
                  </div>
                </div>

                {/* Annual Subscriptions */}
                <div style={{ ...revRow, borderLeft: '3px solid #00d4ff' }}>
                  <div>
                    <div style={modelName}>📆 Annual Subscriptions</div>
                    <div style={modelSub}>SHIELD plan — ₦20,000/year per subscriber</div>
                  </div>
                  <div style={{ fontSize: '0.68rem', opacity: 0.45 }}>
                    Recurring · 6 AI scans/month + unlimited try-ons
                  </div>
                  <div style={{ ...ledgerVal, color: '#00d4ff', fontWeight: '900', textAlign: 'right', fontSize: '0.92rem' }}>
                    {formatNaira(revenueBreakdown.yearlySubRevenue)}
                  </div>
                </div>

                {/* Broadcast Fees */}
                <div style={{ ...revRow, borderLeft: '3px solid #f59e0b' }}>
                  <div>
                    <div style={modelName}>📣 Stylist Broadcast Fees</div>
                    <div style={modelSub}>Stylists pay per campaign to message their client base</div>
                  </div>
                  <div style={{ fontSize: '0.68rem', opacity: 0.45 }}>
                    Charged per broadcast send to subscribed clientele
                  </div>
                  <div style={{ ...ledgerVal, color: '#f59e0b', fontWeight: '900', textAlign: 'right', fontSize: '0.92rem' }}>
                    {formatNaira(broadcastRev)}
                  </div>
                </div>

                {/* Paid Promotions */}
                <div style={{ ...revRow, borderLeft: '3px solid #a78bfa' }}>
                  <div>
                    <div style={modelName}>🚀 Paid Style Promotions</div>
                    <div style={modelSub}>Stylists pay to feature their styles on the ESDRAS homepage</div>
                  </div>
                  <div style={{ fontSize: '0.68rem', opacity: 0.45 }}>
                    Per promoted listing × chosen promotion duration
                  </div>
                  <div style={{ ...ledgerVal, color: '#a78bfa', fontWeight: '900', textAlign: 'right', fontSize: '0.92rem' }}>
                    {formatNaira(promotionRev)}
                  </div>
                </div>

                {/* Total Revenue */}
                <div style={{ ...ledgerTotal, gridTemplateColumns: '2.5fr 2fr 1.2fr', background: 'rgba(46,204,113,0.07)', borderTop: '2px solid rgba(46,204,113,0.22)' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: '900', color: '#2ecc71', letterSpacing: '1px' }}>TOTAL GROSS REVENUE</div>
                  <div />
                  <div style={{ fontSize: '1.2rem', fontWeight: '900', color: '#2ecc71', textAlign: 'right' }}>
                    {formatNaira(totalRevenue)}
                  </div>
                </div>
              </div>

              {/* Mini metrics */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginTop: '16px' }}>
                {[
                  { label: 'BOOKING FEES',     val: formatNaira(platformFees),                       color: '#2ecc71' },
                  { label: 'MONTHLY SUB REV',  val: formatNaira(revenueBreakdown.monthlySubRevenue), color: '#00bbff' },
                  { label: 'YEARLY SUB REV',   val: formatNaira(revenueBreakdown.yearlySubRevenue),  color: '#00d4ff' },
                  { label: 'BROADCAST FEES',   val: formatNaira(broadcastRev),                       color: '#f59e0b' },
                  { label: 'PROMOTION FEES',   val: formatNaira(promotionRev),                       color: '#a78bfa' },
                ].map((item, i) => (
                  <div key={i} style={costMetricItem}>
                    <span style={cmiLabel}>{item.label}</span>
                    <span style={{ ...cmiVal, color: item.color, fontSize: '0.88rem' }}>{item.val}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── AI Cost Ledger ─────────────────────────────────────────── */}
            <Section title="⚡ AI COST LEDGER — CUMULATIVE BURN (ALL TIME)">
              <div style={ledgerTable}>
                <div style={ledgerHeader}>
                  <span>AI MODEL</span>
                  <span>SUCCESSFUL CALLS</span>
                  <span>COST/CALL (USD)</span>
                  <span>COST/CALL (NGN)</span>
                  <span style={{ textAlign: 'right' }}>TOTAL BURN (NGN)</span>
                </div>

                <div style={{ ...ledgerRow, borderLeft: '3px solid #B8860B' }}>
                  <div><div style={modelName}>🧊 Hyper3D Rodin</div><div style={modelSub}>3D Head Reconstruction · Replicate</div></div>
                  <div style={ledgerVal}>{allTimeRodinCount.toLocaleString()}</div>
                  <div style={ledgerVal}>${AI_COSTS_USD.rodin.toFixed(2)}</div>
                  <div style={ledgerVal}>₦{Math.round(AI_COSTS_USD.rodin * usdToNgn).toLocaleString()}</div>
                  <div style={{ ...ledgerVal, color: '#ff6b6b', fontWeight: '900', textAlign: 'right' }}>₦{Math.round(rodinBurnNgn).toLocaleString()}</div>
                </div>

                <div style={{ ...ledgerRow, borderLeft: '3px solid #00d4ff' }}>
                  <div><div style={modelName}>✨ FLUX Fill Pro</div><div style={modelSub}>Hair Inpainting · Replicate</div></div>
                  <div style={ledgerVal}>{allTimeTryonCount.toLocaleString()}</div>
                  <div style={ledgerVal}>${AI_COSTS_USD.flux.toFixed(2)}</div>
                  <div style={ledgerVal}>₦{Math.round(AI_COSTS_USD.flux * usdToNgn).toLocaleString()}</div>
                  <div style={{ ...ledgerVal, color: '#ff6b6b', fontWeight: '900', textAlign: 'right' }}>₦{Math.round(fluxBurnNgn).toLocaleString()}</div>
                </div>

                <div style={{ ...ledgerRow, borderLeft: '3px solid #a78bfa' }}>
                  <div><div style={modelName}>🤖 Claude Sonnet Vision</div><div style={modelSub}>Hair Style Analysis · Anthropic</div></div>
                  <div style={ledgerVal}>{allTimeTryonCount.toLocaleString()}</div>
                  <div style={ledgerVal}>${AI_COSTS_USD.claude.toFixed(3)}</div>
                  <div style={ledgerVal}>₦{Math.round(AI_COSTS_USD.claude * usdToNgn).toLocaleString()}</div>
                  <div style={{ ...ledgerVal, color: '#ff6b6b', fontWeight: '900', textAlign: 'right' }}>₦{Math.round(claudeBurnNgn).toLocaleString()}</div>
                </div>

                <div style={ledgerTotal}>
                  <div style={{ fontSize: '0.72rem', fontWeight: '900', color: GOLD, letterSpacing: '1px' }}>TOTAL CUMULATIVE AI BURN</div>
                  <div />
                  <div style={{ color: GOLD, fontWeight: '700', fontSize: '0.72rem' }}>${totalBurnUsd.toFixed(2)} USD</div>
                  <div />
                  <div style={{ fontSize: '1.15rem', fontWeight: '900', color: '#ff4d4d', textAlign: 'right' }}>₦{Math.round(totalBurnNgn).toLocaleString()}</div>
                </div>
              </div>

              <div style={costMetricsRow}>
                <div style={costMetricItem}><span style={cmiLabel}>COST PER 3D SCAN</span><span style={cmiVal}>₦{Math.round(AI_COSTS_USD.rodin * usdToNgn).toLocaleString()}</span></div>
                <div style={costMetricItem}><span style={cmiLabel}>COST PER TRY-ON</span><span style={cmiVal}>₦{Math.round((AI_COSTS_USD.flux + AI_COSTS_USD.claude) * usdToNgn).toLocaleString()}</span></div>
                <div style={costMetricItem}><span style={cmiLabel}>TOTAL RODIN CALLS</span><span style={cmiVal}>{allTimeRodinCount.toLocaleString()}</span></div>
                <div style={costMetricItem}><span style={cmiLabel}>TOTAL TRY-ON CALLS</span><span style={cmiVal}>{allTimeTryonCount.toLocaleString()}</span></div>
              </div>
            </Section>

            {/* ── Net Profit Summary ─────────────────────────────────────── */}
            <Section title="💰 NET PROFIT SUMMARY">
              <div style={profitSummaryGrid}>
                <div style={{ ...profitRow, borderLeft: '3px solid #2ecc71' }}>
                  <span style={profitLabel}>Total Gross Revenue (Platform + Subs + Broadcast + Promo)</span>
                  <span style={{ ...profitVal, color: '#2ecc71' }}>{formatNaira(totalRevenue)}</span>
                </div>
                <div style={{ ...profitRow, borderLeft: '3px solid #ff4d4d' }}>
                  <span style={profitLabel}>Less: Total AI Burn (Rodin + FLUX + Claude)</span>
                  <span style={{ ...profitVal, color: '#ff4d4d' }}>− {formatNaira(aiBurnRate)}</span>
                </div>
                <div style={{ ...profitRow, background: netProfit >= 0 ? 'rgba(46,204,113,0.1)' : 'rgba(255,77,77,0.1)', borderLeft: `3px solid ${netProfit >= 0 ? '#2ecc71' : '#ff4d4d'}` }}>
                  <span style={{ ...profitLabel, fontWeight: '900', fontSize: '0.82rem', color: 'white', opacity: 1 }}>EST. NET PROFIT</span>
                  <span style={{ ...profitVal, color: netProfit >= 0 ? '#2ecc71' : '#ff4d4d', fontSize: '1.4rem' }}>{formatNaira(netProfit)}</span>
                </div>
              </div>
            </Section>

            {/* Broadcast + Disputes */}
            <Section title="URGENT BROADCAST (PUSH ALL USERS)">
              <div style={{ display: 'flex', gap: '10px' }}>
                <input style={inputStyle} placeholder="Type system-wide alert..." value={broadcastMessage} onChange={e => setBroadcastMessage(e.target.value)} />
                <button style={activeBtn} onClick={sendGlobalBroadcast}>SEND BROADCAST</button>
              </div>
            </Section>

            <div style={bottomGrid}>
              <Section title="PENDING WITHDRAWALS">
                {payouts.length === 0 ? <p style={{ opacity: 0.5 }}>All clear.</p> : payouts.map(p => (
                  <div key={p.id} style={cardItem}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ margin: 0 }}><strong>{p.stylistName}</strong></p>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: GOLD }}>{formatNaira(p.amount)}</p>
                        <p style={{ margin: 0, fontSize: '0.6rem', opacity: 0.6 }}>{p.bankName} | {p.accountNumber}</p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {p.status === 'pending'
                          ? <button style={miniApproveBtn} onClick={() => processPayoutRequest(p.id)}>PROCESS</button>
                          : <button style={{ ...miniApproveBtn, background: GOLD, color: '#000' }} onClick={() => finalApprovePayout(p)}>CONFIRM PAID</button>
                        }
                        <button style={miniRejectBtn} onClick={() => rejectPayoutRequest(p.id)}>DENY</button>
                      </div>
                    </div>
                  </div>
                ))}
              </Section>
              <Section title="ESCROW SHIELD DISPUTES">
                {disputes.length === 0 ? <p style={{ opacity: 0.5 }}>No disputes.</p> : disputes.map(d => (
                  <div key={d.id} style={cardItem}>
                    <p>{d.clientName} vs {d.stylistName}</p>
                    <div style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
                      <button style={miniApproveBtn}>Release</button>
                      <button style={miniRejectBtn}>Refund</button>
                    </div>
                  </div>
                ))}
              </Section>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            LIVE TELEMETRY TAB
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'telemetry' && (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
            <Section title="ACTIVE PROJECTION NODES">
              <div style={telemetryGrid}>
                {liveSessions.length === 0 ? (
                  <div style={idleState}>SYSTEM IDLE</div>
                ) : liveSessions.map(session => (
                  <div key={session.id} style={liveCard}>
                    <div style={cardHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={pulseDot} />
                        <span style={statusText}>LIVE STREAM</span>
                      </div>
                      <button onClick={() => terminateLiveSession(session.id)} style={killBtn}>KILL</button>
                    </div>
                    <div style={dataGrid}>
                      <div style={dataItem}><label>NODE</label><span>{session.id.slice(0,8)}</span></div>
                      <div style={dataItem}><label>LOAD</label><span>{Math.round((session.intensity || 0) * 100)}%</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
            <Section title="SYSTEM LOGS">
              <div style={logsContainer}>
                <div style={logEntry}>[SECURE] Protocol active...</div>
                {liveSessions.length > 0 && <div style={logEntry}>[DATA] Syncing {liveSessions.length} nodes...</div>}
              </div>
            </Section>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            AI OPS TAB — THREE MODEL CHARTS + BAR / CURVE TOGGLE
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'ai_ops' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>

            {/* Header + controls */}
            <div style={aiOpsHeader}>
              <div>
                <h3 style={{ color: GOLD, fontSize: '0.92rem', margin: '0 0 4px', letterSpacing: '2px' }}>
                  AI MODEL PERFORMANCE DASHBOARD
                </h3>
                <p style={{ fontSize: '0.6rem', opacity: 0.4, margin: 0 }}>
                  Real-time cost tracking per model · All values in Naira · $1 = ₦{usdToNgn.toLocaleString('en-NG', { maximumFractionDigits: 0 })} live
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Timeframe buttons */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  {['daily','weekly','monthly','alltime'].map(tf => (
                    <button key={tf} onClick={() => setAiOpsTimeframe(tf)} style={timeBtn(aiOpsTimeframe === tf)}>
                      {tf === 'daily' ? '7D' : tf === 'weekly' ? '4W' : tf === 'monthly' ? '12M' : 'ALL'}
                    </button>
                  ))}
                </div>
                {/* Bar / Curve toggle */}
                <div style={chartToggleWrap}>
                  <button
                    onClick={() => setChartType('bar')}
                    style={{ ...chartToggleBtn, background: chartType === 'bar' ? GOLD : 'transparent', color: chartType === 'bar' ? '#000' : GOLD }}
                  >
                    ▌▌▌ BAR
                  </button>
                  <button
                    onClick={() => setChartType('curve')}
                    style={{ ...chartToggleBtn, background: chartType === 'curve' ? GOLD : 'transparent', color: chartType === 'curve' ? '#000' : GOLD }}
                  >
                    ∿ CURVE
                  </button>
                </div>
              </div>
            </div>

            {/* Period summary bar */}
            <div style={periodSummaryBar}>
              <div style={{ ...periodSummaryItem, borderRight: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={psiLabel}>PERIOD</span>
                <span style={{ ...psiVal, fontSize: '0.72rem', color: GOLD }}>{tfLabel}</span>
              </div>
              <div style={{ ...periodSummaryItem, borderRight: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={psiLabel}>🧊 Rodin Spend</span>
                <span style={{ ...psiVal, color: '#B8860B' }}>₦{formatCompact(rodinPeriodCost)}</span>
              </div>
              <div style={{ ...periodSummaryItem, borderRight: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={psiLabel}>✨ FLUX Spend</span>
                <span style={{ ...psiVal, color: '#00d4ff' }}>₦{formatCompact(fluxPeriodCost)}</span>
              </div>
              <div style={{ ...periodSummaryItem, borderRight: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={psiLabel}>🤖 Claude Spend</span>
                <span style={{ ...psiVal, color: '#a78bfa' }}>₦{formatCompact(claudePeriodCost)}</span>
              </div>
              <div style={periodSummaryItem}>
                <span style={psiLabel}>TOTAL PERIOD BURN</span>
                <span style={{ ...psiVal, color: '#ff4d4d', fontSize: '1.1rem' }}>₦{formatCompact(totalPeriodCost)}</span>
              </div>
            </div>

            {/* Chart 1: Hyper3D Rodin */}
            <div style={aiChartCard}>
              <div style={aiChartHeader}>
                <div>
                  <span style={aiChartTitle}>🧊 HYPER3D RODIN — 3D HEAD RECONSTRUCTION</span>
                  <span style={aiChartSub}>
                    Replicate · ${AI_COSTS_USD.rodin.toFixed(2)}/call · ₦{Math.round(AI_COSTS_USD.rodin * usdToNgn).toLocaleString()}/call ·
                    {chartType === 'bar' ? ' Bars = call count, height = Naira cost' : ' Curve = Naira spend over time, dotted = call volume'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={aiCallBadge('#B8860B')}>{rodinData.reduce((s,d)=>s+d.calls,0)} calls</div>
                  <div style={aiCostBadge}>₦{formatCompact(rodinPeriodCost)} period</div>
                  <div style={aiLifetimeBadge}>₦{formatCompact(rodinBurnNgn)} lifetime</div>
                </div>
              </div>
              {chartType === 'bar'
                ? <AIModelBarChart   data={rodinData}  color="#B8860B" gradientId="grad-rodin"  emptyMsg="No 3D scan calls in this period — data appears once users complete scans" />
                : <AIModelCurveChart data={rodinData}  color="#B8860B" gradientId="cgrad-rodin" emptyMsg="No 3D scan calls in this period" />
              }
            </div>

            {/* Chart 2: FLUX Fill Pro */}
            <div style={aiChartCard}>
              <div style={aiChartHeader}>
                <div>
                  <span style={aiChartTitle}>✨ FLUX FILL PRO — HAIR INPAINTING</span>
                  <span style={aiChartSub}>
                    Replicate · ${AI_COSTS_USD.flux.toFixed(2)}/call · ₦{Math.round(AI_COSTS_USD.flux * usdToNgn).toLocaleString()}/call · Triggered per style try-on
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={aiCallBadge('#00d4ff')}>{fluxData.reduce((s,d)=>s+d.calls,0)} calls</div>
                  <div style={aiCostBadge}>₦{formatCompact(fluxPeriodCost)} period</div>
                  <div style={aiLifetimeBadge}>₦{formatCompact(fluxBurnNgn)} lifetime</div>
                </div>
              </div>
              {chartType === 'bar'
                ? <AIModelBarChart   data={fluxData}  color="#00d4ff" gradientId="grad-flux"  emptyMsg="No style try-on calls in this period — data appears once users try hairstyles" />
                : <AIModelCurveChart data={fluxData}  color="#00d4ff" gradientId="cgrad-flux" emptyMsg="No style try-on calls in this period" />
              }
            </div>

            {/* Chart 3: Claude Vision */}
            <div style={aiChartCard}>
              <div style={aiChartHeader}>
                <div>
                  <span style={aiChartTitle}>🤖 CLAUDE SONNET — VISION HAIR ANALYSIS</span>
                  <span style={aiChartSub}>
                    Anthropic · ${AI_COSTS_USD.claude.toFixed(3)}/call · ₦{Math.round(AI_COSTS_USD.claude * usdToNgn).toLocaleString()}/call · Runs once per try-on alongside FLUX
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={aiCallBadge('#a78bfa')}>{claudeData.reduce((s,d)=>s+d.calls,0)} calls</div>
                  <div style={aiCostBadge}>₦{formatCompact(claudePeriodCost)} period</div>
                  <div style={aiLifetimeBadge}>₦{formatCompact(claudeBurnNgn)} lifetime</div>
                </div>
              </div>
              {chartType === 'bar'
                ? <AIModelBarChart   data={claudeData}  color="#a78bfa" gradientId="grad-claude"  emptyMsg="No Claude vision calls in this period — runs alongside every FLUX try-on" />
                : <AIModelCurveChart data={claudeData}  color="#a78bfa" gradientId="cgrad-claude" emptyMsg="No Claude vision calls in this period" />
              }
            </div>

          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            SUPPORT TAB
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'support' && (
          <Section title="TICKET CONCIERGE">
            {tickets.length === 0 ? <p>No active tickets.</p> : tickets.map(t => (
              <div key={t.id} style={{ ...cardItem, borderLeft: t.status === 'open' ? '4px solid #ff4d4d' : '4px solid #2ecc71' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', color: GOLD }}>{t.subject}</span>
                  <span style={{ fontSize: '0.6rem', opacity: 0.5 }}>{t.id}</span>
                </div>
                <p style={{ fontSize: '0.75rem', marginBottom: '15px' }}>{t.userName} ({t.userEmail})</p>
                <div style={{ background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px', maxHeight: '150px', overflowY: 'auto', marginBottom: '15px' }}>
                  {t.messages?.map((m, idx) => (
                    <div key={idx} style={{ marginBottom: '8px', textAlign: m.sender === 'ADMIN' ? 'right' : 'left' }}>
                      <p style={{ fontSize: '0.7rem', color: m.sender === 'ADMIN' ? GOLD : '#fff', margin: 0 }}>{m.sender}</p>
                      <p style={{ fontSize: '0.85rem', margin: 0 }}>{m.text}</p>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input id={`reply-${t.id}`} style={inputStyle} placeholder="Type response..." />
                  <button style={miniApproveBtn} onClick={() => {
                    const el = document.getElementById(`reply-${t.id}`);
                    handleTicketReply(t.id, el.value);
                    el.value = '';
                  }}>REPLY</button>
                  <button style={miniRejectBtn} onClick={() => closeTicket(t.id)}>CLOSE</button>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            STAFF TAB
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'staff' && isSuper && (
          <Section title="STAFF MODERATION">
            {adminUsers.map(admin => (
              <div key={admin.id} style={cardItem}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>{admin.email}</p>
                    <p style={{ fontSize: '0.6rem', opacity: 0.5 }}>UID: {admin.id}</p>
                  </div>
                  <button onClick={() => revokeAdminAccess(admin.id)} style={miniRejectBtn}>REVOKE ALL</button>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  <AdminPermToggle userId={admin.id} label="Finances" current={admin.adminAccessLevel?.finances} />
                  <AdminPermToggle userId={admin.id} label="Support"  current={admin.adminAccessLevel?.support}  />
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            USERS / MODERATION TAB
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'users' && (
          <Section title="USER MODERATION">
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <input style={inputStyle} placeholder="Search email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              <button style={activeBtn} onClick={async () => {
                const q = query(collection(db, 'users'), where('email', '==', searchQuery));
                const s = await getDocs(q);
                setSearchResults(s.docs.map(d => ({ id: d.id, ...d.data() })));
              }}>FIND</button>
            </div>
            {searchResults.map(u => (
              <div key={u.id} style={cardItem}>
                <p>{u.email} | Scans: {u.scansUsed || 0} | Role: {u.role}</p>
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button style={miniApproveBtn} onClick={() => updateDoc(doc(db, 'users', u.id), { scansUsed: 0 })}>RESET SCANS</button>
                  <button style={miniApproveBtn} onClick={() => grantAdminAccess(u.id)}>APPOINT ADMIN</button>
                </div>
              </div>
            ))}
          </Section>
        )}

      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  AI MODEL BAR CHART COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
function AIModelBarChart({ data, color, gradientId, emptyMsg }) {
  const W = 700, H = 185, PAD_X = 10, TOP_PAD = 24, BOT_PAD = 22;
  const barArea = H - TOP_PAD - BOT_PAD;

  if (!data || data.length === 0) {
    return <div style={emptyChartMsg}>{emptyMsg || 'No data for selected period'}</div>;
  }

  const maxCost   = Math.max(...data.map(d => d.costNgn), 1);
  const totalBars = data.length;
  const slotW     = (W - PAD_X * 2) / totalBars;
  const innerW    = Math.max(slotW * 0.70, 6);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '200px', display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   style={{ stopColor: color, stopOpacity: 0.9 }} />
          <stop offset="100%" style={{ stopColor: color, stopOpacity: 0.05 }} />
        </linearGradient>
      </defs>

      {/* Grid lines */}
      {[0.25,0.5,0.75,1].map((frac, i) => {
        const y = TOP_PAD + (1 - frac) * barArea;
        return (
          <g key={i}>
            <line x1={PAD_X} y1={y} x2={W - PAD_X} y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
            <text x={PAD_X + 2} y={y - 3} fill="#3a3a3a" fontSize="7.5">₦{formatCompact(maxCost * frac)}</text>
          </g>
        );
      })}

      {/* Bars */}
      {data.map((point, i) => {
        const bH = Math.max((point.costNgn / maxCost) * barArea, point.calls > 0 ? 2 : 0);
        const bX = PAD_X + i * slotW + (slotW - innerW) / 2;
        const bY = TOP_PAD + barArea - bH;
        const showXLabel = totalBars <= 14 || i % Math.ceil(totalBars / 14) === 0;
        return (
          <g key={i}>
            {bH > 0 && <rect x={bX} y={bY} width={innerW} height={bH} fill={`url(#${gradientId})`} rx="3" />}
            {bH > 0 && <line x1={bX} y1={bY} x2={bX + innerW} y2={bY} stroke={color} strokeWidth="1.5" opacity="0.85" />}
            {point.calls > 0 && (
              <text x={bX + innerW / 2} y={Math.max(bY - 5, TOP_PAD - 6)} textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="9.5" fontWeight="bold">
                {point.calls}
              </text>
            )}
            {bH > 26 && (
              <text x={bX + innerW / 2} y={bY + bH / 2 + 4} textAnchor="middle" fill="rgba(0,0,0,0.55)" fontSize="8" fontWeight="bold">
                ₦{formatCompact(point.costNgn)}
              </text>
            )}
            {showXLabel && (
              <text x={bX + innerW / 2} y={H - 4} textAnchor="middle" fill="#4a4a4a" fontSize="7.5">{point.label}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  AI MODEL CURVE CHART COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
function AIModelCurveChart({ data, color, gradientId, emptyMsg }) {
  const W = 700, H = 210, PAD_X = 44, PAD_TOP = 28, PAD_BOT = 34;
  const plotW = W - PAD_X * 2;
  const plotH = H - PAD_TOP - PAD_BOT;

  if (!data || data.length === 0) {
    return <div style={emptyChartMsg}>{emptyMsg || 'No data for selected period'}</div>;
  }

  const maxCost  = Math.max(...data.map(d => d.costNgn), 1);
  const maxCalls = Math.max(...data.map(d => d.calls), 1);
  const n        = data.length;

  const costPoints = data.map((d, i) => ({
    x: PAD_X + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW),
    y: PAD_TOP + plotH - (d.costNgn / maxCost) * plotH,
  }));

  const callPoints = data.map((d, i) => ({
    x: PAD_X + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW),
    y: PAD_TOP + plotH - (d.calls / maxCalls) * plotH,
  }));

  const costPath = n === 1
    ? `M ${costPoints[0].x - 20} ${costPoints[0].y} L ${costPoints[0].x + 20} ${costPoints[0].y}`
    : makeBezierPath(costPoints);

  const callPath = n === 1
    ? `M ${callPoints[0].x - 20} ${callPoints[0].y} L ${callPoints[0].x + 20} ${callPoints[0].y}`
    : makeBezierPath(callPoints);

  const areaPath = n === 1
    ? `M ${PAD_X} ${PAD_TOP + plotH} L ${costPoints[0].x} ${costPoints[0].y} L ${W - PAD_X} ${PAD_TOP + plotH} Z`
    : `${costPath} L ${PAD_X + plotW} ${PAD_TOP + plotH} L ${PAD_X} ${PAD_TOP + plotH} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '220px', display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   style={{ stopColor: color, stopOpacity: 0.32 }} />
          <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
        </linearGradient>
        <filter id={`glow-${gradientId}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Y-axis grid lines + labels */}
      {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => {
        const y = PAD_TOP + plotH - frac * plotH;
        return (
          <g key={i}>
            <line x1={PAD_X} y1={y} x2={W - PAD_X} y2={y}
              stroke={frac === 0 ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)'}
              strokeWidth="1"
              strokeDasharray={frac > 0 && frac < 1 ? '4,4' : ''}
            />
            <text x={PAD_X - 4} y={y + 3.5} textAnchor="end" fill="#555" fontSize="8">
              ₦{formatCompact(maxCost * frac)}
            </text>
          </g>
        );
      })}

      {/* Area fill */}
      <path d={areaPath} fill={`url(#${gradientId})`} />

      {/* Call count reference curve (dotted, muted) */}
      <path d={callPath} fill="none" stroke={color} strokeWidth="1" strokeDasharray="5,4" opacity="0.22" />

      {/* Primary cost curve with glow */}
      <path d={costPath} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" filter={`url(#glow-${gradientId})`} />

      {/* Dots + value labels */}
      {costPoints.map((pt, i) => {
        const d         = data[i];
        const showLabel = n <= 12 || i % Math.max(1, Math.ceil(n / 8)) === 0 || i === n - 1;
        const labelUp   = pt.y > PAD_TOP + 28;
        return (
          <g key={i}>
            {/* Outer glow ring */}
            <circle cx={pt.x} cy={pt.y} r="6.5" fill={color} opacity="0.12" />
            {/* Dot */}
            <circle cx={pt.x} cy={pt.y} r="3.5" fill={color} opacity="0.92" />

            {/* Naira value label */}
            {showLabel && d.costNgn > 0 && (
              <g>
                <rect x={pt.x - 24} y={labelUp ? pt.y - 23 : pt.y + 6} width="48" height="13" rx="4" fill="rgba(0,0,0,0.75)" />
                <text
                  x={pt.x} y={labelUp ? pt.y - 13 : pt.y + 16}
                  textAnchor="middle" fill={color} fontSize="8.5" fontWeight="bold"
                >
                  ₦{formatCompact(d.costNgn)}
                </text>
              </g>
            )}

            {/* Call count (below axis) */}
            {showLabel && d.calls > 0 && (
              <text x={pt.x} y={H - 18} textAnchor="middle" fill="rgba(255,255,255,0.22)" fontSize="7">
                {d.calls}×
              </text>
            )}

            {/* X-axis time label */}
            {showLabel && (
              <text x={pt.x} y={H - 5} textAnchor="middle" fill="#4a4a4a" fontSize="7.5">{d.label}</text>
            )}
          </g>
        );
      })}

      {/* Legend */}
      <line x1={PAD_X} y1={H - 1} x2={PAD_X + 28} y2={H - 1} stroke={color} strokeWidth="2.5" />
      <text x={PAD_X + 32} y={H + 0.5} fill="#555" fontSize="7.5">Naira spend</text>
      <line x1={PAD_X + 94} y1={H - 1} x2={PAD_X + 122} y2={H - 1} stroke={color} strokeWidth="1" strokeDasharray="5,4" opacity="0.35" />
      <text x={PAD_X + 126} y={H + 0.5} fill="#555" fontSize="7.5">Call volume (scaled)</text>
    </svg>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  HELPER COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════
const AdminPermToggle = ({ userId, label, current }) => {
  const handleToggle = async () => {
    await updateDoc(doc(db, 'users', userId), { [`adminAccessLevel.${label.toLowerCase()}`]: !current });
    toast.success(`${label} Updated`);
  };
  return (
    <button onClick={handleToggle} style={{ padding: '6px 15px', fontSize: '0.65rem', borderRadius: '6px', border: 'none', cursor: 'pointer', background: current ? GOLD : '#111', color: current ? '#000' : '#888', fontWeight: 'bold' }}>
      {label.toUpperCase()}: {current ? 'ENABLED' : 'DISABLED'}
    </button>
  );
};

const Section = ({ title, children }) => (
  <div style={boxStyle}>
    <h4 style={boxTitle}>{title}</h4>
    {children}
  </div>
);

const KPIBox = ({ label, value, color }) => (
  <div style={miniStat}>
    <h2 style={{ fontSize: '1.35rem', color: color || '#fff', margin: 0, fontWeight: '900' }}>{value}</h2>
    <p style={kpiLabelStyle}>{label}</p>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
//  STYLES
// ═════════════════════════════════════════════════════════════════════════════
const containerStyle   = { minHeight: '100vh', background: NAVY, color: 'white', fontFamily: 'Montserrat, sans-serif' };
const topNav           = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 30px', background: '#000', borderBottom: `1px solid ${GOLD}`, position: 'sticky', top: 0, zIndex: 100, gap: '16px', flexWrap: 'wrap' };
const navBtn           = { background: 'none', border: 'none', color: GOLD, cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '1px' };
const kpiGrid          = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '16px' };
const miniStat         = { background: 'rgba(255,255,255,0.05)', padding: '1.4rem', borderRadius: '20px', textAlign: 'center', border: '1px solid rgba(184,134,11,0.2)' };
const boxStyle         = { background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: '16px', border: '1px solid rgba(184,134,11,0.15)' };
const boxTitle         = { color: GOLD, fontSize: '0.9rem', fontWeight: '700', marginBottom: '1.5rem', letterSpacing: '2px' };
const cardItem         = { background: 'rgba(0,0,0,0.3)', padding: '1.2rem', borderRadius: '12px', marginBottom: '1rem', border: '1px solid rgba(255,255,255,0.05)' };
const activeBtn        = { background: GOLD, color: 'black', border: 'none', padding: '10px 20px', borderRadius: '50px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem' };
const inactiveBtn      = { background: 'rgba(184,134,11,0.1)', color: GOLD, border: 'none', padding: '10px 20px', borderRadius: '50px', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' };
const inputStyle       = { background: '#000', border: '1px solid #333', color: 'white', padding: '12px', borderRadius: '12px', flex: 1, outline: 'none', fontSize: '0.8rem' };
const miniApproveBtn   = { background: '#2ecc71', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 'bold' };
const miniRejectBtn    = { background: '#e74c3c', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 'bold' };
const exportBtn        = { background: GOLD, color: 'black', padding: '8px 20px', borderRadius: '50px', fontWeight: 'bold', border: 'none', cursor: 'pointer', fontSize: '0.8rem' };
const lockdownBtn      = { background: 'none', border: '1px solid', color: '#fff', padding: '6px 15px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 'bold' };
const fullPageCenter   = { height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: NAVY, color: GOLD, letterSpacing: '2px' };
const inlineBadge      = { background: '#ff4d4d', color: 'white', borderRadius: '50%', padding: '2px 8px', fontSize: '0.6rem', marginLeft: '8px' };
const kpiLabelStyle    = { opacity: 0.6, fontSize: '0.65rem', marginTop: '0.5rem', textTransform: 'uppercase', letterSpacing: '1px' };
const bottomGrid       = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' };
const telemetryGrid    = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px' };
const liveCard         = { background: '#080c12', padding: '15px', borderRadius: '8px', borderLeft: `3px solid ${GOLD}` };
const pulseDot         = { width: '8px', height: '8px', backgroundColor: '#00ff00', borderRadius: '50%', animation: 'pulse-live 2s infinite' };
const killBtn          = { background: 'none', border: '1px solid #ff444433', color: '#ff4444', fontSize: '0.5rem', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer' };
const logsContainer    = { height: '300px', overflowY: 'auto', background: 'black', padding: '15px', borderRadius: '8px', fontFamily: 'monospace' };
const logEntry         = { fontSize: '0.65rem', color: '#4caf50', marginBottom: '6px' };
const idleState        = { opacity: 0.3, textAlign: 'center', padding: '20px' };
const cardHeader       = { display: 'flex', justifyContent: 'space-between', marginBottom: '10px' };
const dataGrid         = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' };
const dataItem         = { display: 'flex', flexDirection: 'column', fontSize: '0.65rem' };
const statusText       = { fontSize: '0.6rem', color: GOLD, fontWeight: 'bold' };
const timeBtn = (active) => ({ background: active ? GOLD : 'transparent', color: active ? 'black' : GOLD, border: `1px solid ${GOLD}`, padding: '6px 14px', borderRadius: '6px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.15s' });

// ── AI cost tracking styles ───────────────────────────────────────────────────
const rateBadge        = { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px', background: 'rgba(184,134,11,0.08)', border: `1px solid rgba(184,134,11,0.22)`, borderRadius: '8px', padding: '6px 12px' };
const rateInfoBar      = { display: 'flex', alignItems: 'center', gap: '16px', background: 'rgba(184,134,11,0.05)', border: `1px solid rgba(184,134,11,0.12)`, borderRadius: '10px', padding: '10px 16px', marginBottom: '18px', flexWrap: 'wrap' };
const ledgerTable      = { display: 'flex', flexDirection: 'column', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)' };
const ledgerHeader     = { display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1.3fr', gap: '12px', background: 'rgba(255,255,255,0.04)', padding: '10px 18px', fontSize: '0.55rem', fontWeight: '900', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' };
const ledgerRow        = { display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1.3fr', gap: '12px', background: 'rgba(0,0,0,0.22)', padding: '14px 18px', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.04)' };
const ledgerTotal      = { display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1.3fr', gap: '12px', background: 'rgba(184,134,11,0.07)', padding: '16px 18px', alignItems: 'center', borderTop: `2px solid rgba(184,134,11,0.25)` };
const ledgerVal        = { fontSize: '0.8rem', fontWeight: '600', color: 'rgba(255,255,255,0.82)' };
const modelName        = { fontSize: '0.78rem', fontWeight: '800', color: 'white', marginBottom: '2px' };
const modelSub         = { fontSize: '0.54rem', opacity: 0.4, letterSpacing: '0.3px' };
const costMetricsRow   = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginTop: '18px' };
const costMetricItem   = { display: 'flex', flexDirection: 'column', gap: '5px', background: 'rgba(0,0,0,0.28)', padding: '12px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' };
const cmiLabel         = { fontSize: '0.54rem', opacity: 0.4, letterSpacing: '1px', textTransform: 'uppercase' };
const cmiVal           = { fontSize: '1rem', fontWeight: '900', color: GOLD };
const aiOpsHeader      = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.02)', border: `1px solid rgba(184,134,11,0.14)`, borderRadius: '14px', padding: '18px 22px', flexWrap: 'wrap', gap: '14px' };
const periodSummaryBar = { display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 1fr 1fr', background: 'rgba(0,0,0,0.32)', border: `1px solid rgba(184,134,11,0.13)`, borderRadius: '12px', overflow: 'hidden' };
const periodSummaryItem= { display: 'flex', flexDirection: 'column', gap: '5px', padding: '14px 18px' };
const psiLabel         = { fontSize: '0.54rem', opacity: 0.38, textTransform: 'uppercase', letterSpacing: '1px' };
const psiVal           = { fontSize: '0.95rem', fontWeight: '900', color: 'white' };
const aiChartCard      = { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(184,134,11,0.11)', borderRadius: '16px', padding: '20px 22px 14px', overflow: 'hidden' };
const aiChartHeader    = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' };
const aiChartTitle     = { display: 'block', fontSize: '0.76rem', fontWeight: '900', color: 'white', letterSpacing: '1.5px', marginBottom: '5px' };
const aiChartSub       = { display: 'block', fontSize: '0.57rem', opacity: 0.38, letterSpacing: '0.3px', lineHeight: 1.6 };
const aiCallBadge  = (c) => ({ background: `${c}1a`, border: `1px solid ${c}44`, color: c, borderRadius: '6px', padding: '4px 10px', fontSize: '0.64rem', fontWeight: '700', whiteSpace: 'nowrap' });
const aiCostBadge      = { background: 'rgba(255,77,77,0.1)', border: '1px solid rgba(255,77,77,0.28)', color: '#ff7070', borderRadius: '6px', padding: '4px 10px', fontSize: '0.64rem', fontWeight: '700', whiteSpace: 'nowrap' };
const aiLifetimeBadge  = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', color: 'rgba(255,255,255,0.42)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.6rem', fontWeight: '600', whiteSpace: 'nowrap' };
const emptyChartMsg    = { textAlign: 'center', padding: '36px 20px', opacity: 0.3, fontSize: '0.68rem', letterSpacing: '0.5px', lineHeight: 1.7 };
const chartToggleWrap  = { display: 'flex', border: `1px solid ${GOLD}`, borderRadius: '8px', overflow: 'hidden' };
const chartToggleBtn   = { padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: '0.64rem', fontWeight: '800', letterSpacing: '0.5px', transition: 'all 0.15s' };

// ── Revenue breakdown styles ──────────────────────────────────────────────────
const revRow           = { display: 'grid', gridTemplateColumns: '2.5fr 2fr 1.2fr', gap: '12px', background: 'rgba(0,0,0,0.22)', padding: '14px 18px', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.04)' };
const profitSummaryGrid= { display: 'flex', flexDirection: 'column', gap: '10px' };
const profitRow        = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: 'rgba(0,0,0,0.25)', borderRadius: '10px' };
const profitLabel      = { fontSize: '0.72rem', opacity: 0.6, letterSpacing: '0.3px' };
const profitVal        = { fontSize: '1.05rem', fontWeight: '900' };
